import { loadScript } from '../theme-scripts.js';

if (!customElements.get('vimeo-video')) {
  customElements.define(
    'vimeo-video',
    class VimeoVideo extends HTMLElement {
      constructor() {
        super();
        this.initScript();
      }

      initScript() {
        loadScript('https://player.vimeo.com/api/player.js')
          .then(() => {
            this.initVimeo();
          })
          .catch((error) => {
            console.error('Failed to load Vimeo player script:', error);
          });
      }

      initVimeo() {
        this.vimeoVideo = this;
        this.vimeoVideoSettings = JSON.parse(this.vimeoVideo.querySelector('[data-vimeo-json-settings]').innerHTML);
        this.vimeoPlaceholder = this.vimeoVideo.querySelector('.vimeo-video__placeholder');

        const mediaQueryList = window.matchMedia(`(min-width: ${this.vimeoVideoSettings['videoBreakpoint']})`);

        this.matchQueryInit(mediaQueryList);
        mediaQueryList.addEventListener('change', this.matchQueryChange.bind(this));

        this.unmuteButton = this.vimeoVideo.querySelector('.vimeo-video__unmute-button');
        if (this.unmuteButton) {
          this.unmuteButton.addEventListener('click', this.enableSound.bind(this));
        }

        this.pausePlayButton = this.vimeoVideo.querySelector('.video-play-pause-btn');
        if (this.pausePlayButton) {
          this.pausePlayButton.addEventListener('click', this.togglePlayback.bind(this));
        }
      }

      togglePlayback() {
        const isPlaying = this.pausePlayButton.dataset.playing === 'true';
        // Track explicit user intent so the visibility observer below doesn't
        // auto-resume a video the visitor deliberately paused.
        this.manuallyPaused = isPlaying;
        if (isPlaying) {
          this.player.pause();
        } else {
          this.player.play();
        }
        this.pausePlayButton.dataset.playing = isPlaying ? 'false' : 'true';
        this.pausePlayButton.setAttribute('aria-label', isPlaying ? 'Play video' : 'Pause video');
      }

      matchQueryInit(event) {
        const device = event.matches ? 'Desktop' : 'Mobile';
        if (event.matches) {
          this.setupPlayer(device);
        } else {
          this.setupPlayer(device);
        }
      }

      matchQueryChange(event) {
        this.player.unload();
        const device = event.matches ? 'Desktop' : 'Mobile';
        if (event.matches) {
          this.player.loadVideo(this.vimeoVideoSettings['videoId' + device]);
        } else {
          this.player.loadVideo(this.vimeoVideoSettings['videoId' + device]);
        }
      }

      setupPlayer(device) {
        this.player = new Vimeo.Player(this.vimeoPlaceholder, {
          url: this.vimeoVideoSettings['videoId' + `${device}`],
          autoplay: this.vimeoVideoSettings['autoplay'],
          autopause: this.vimeoVideoSettings['autopause'],
          muted: this.vimeoVideoSettings['muted'],
          background: this.vimeoVideoSettings['background'],
          loop: this.vimeoVideoSettings['loop'],
          title: this.vimeoVideoSettings['title'],
          controls: this.vimeoVideoSettings['controls'],
        });
        this.playPlayer();
      }

      playPlayer() {
        // Make sure the video is paused when loaded in
        this.player.pause();

        this.player.ready().then(
          function () {
            // Ensure the dynamically created iframe always has a title so
            // screen readers can identify the frame (WCAG 4.1.2 / Issue 9).
            const iframe = this.vimeoPlaceholder.querySelector('iframe');
            if (iframe && !iframe.getAttribute('title')) {
              iframe.setAttribute('title', 'Background video');
            }

            var observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  this.player.getPaused().then(
                    function (paused) {
                      const fallbackImage = this.querySelector('.vimeo-video__slide-fallback-image');
                      if (!entry.isIntersecting && !paused) {
                        if (fallbackImage) {
                          fallbackImage.style.opacity = 1;
                          fallbackImage.style.visibility = 'visible';
                        }
                        this.player.pause();
                      } else if (entry.isIntersecting && paused) {
                        // Respect a manual pause — don't auto-resume on scroll.
                        if (this.manuallyPaused) return;
                        if (fallbackImage) {
                          fallbackImage.style.opacity = 0;
                          fallbackImage.style.visibility = 'hidden';
                        }
                        this.player.play();
                      }
                    }.bind(this),
                  );
                });
              },
              { threshold: 0.2 },
            );
            observer.observe(this);
          }.bind(this),
        );
      }

      enableSound() {
        this.player.setVolume(1);
        this.unmuteButton.classList.add('hidden');
      }
    },
  );
}
